/**
 * The status colours MUI draws as text (SF-217 increment 1, inventories section C).
 *
 * MUI uses `palette.<key>.main` as text in outlined chips, text buttons, `Typography color=` and
 * alerts, so `main` must reach 4.5:1 on paper, surface and sunken. The design's status tokens are
 * tuned for pictograms at 3:1; in light mode they already reach 4.5:1 and are used as they are. In
 * dark mode, running and failed do not (running is 3.13:1 on the surface), so `main` is a lighter
 * step of the same OKLCH hue and chroma. Every value here was derived from the tokens by a recorded
 * script, and ./__tests__/statusColours.test.ts holds each pairing.
 *
 * - `main`: text, outlines and fills;
 * - `contrastText`: text on a `main` fill (white in light mode, ink in dark);
 * - `dark`: a contained control's hover, 0.06 of OKLCH lightness away from the ground;
 * - `light`: a quiet fill behind ink or `main` text, mixed into the surface by the design's own
 *   `--accent-wash` recipe (16.7% in light, 2.15% in dark, in linear light).
 *
 * The pictogram colours stay as they are, in `palette.status` (./customerTheme.ts).
 */
import type { ThemeMode } from './tokens';

export const STATUS_KEYS = ['success', 'info', 'warning', 'error'] as const;
export type StatusKey = (typeof STATUS_KEYS)[number];

export interface StatusColour {
  main: string;
  light: string;
  dark: string;
  contrastText: string;
}

/** success = completed, info = running, warning = late and waiting (the accent's ink), error = failed. */
const LIGHT: Record<StatusKey, StatusColour> = {
  success: { main: '#116b1a', light: '#ebeeeb', dark: '#01580d', contrastText: '#ffffff' },
  info: { main: '#1949c5', light: '#ebedf7', dark: '#0835b1', contrastText: '#ffffff' },
  warning: { main: '#b43f0e', light: '#f5eceb', dark: '#9b3100', contrastText: '#ffffff' },
  error: { main: '#c0262d', light: '#f6ecec', dark: '#ab001a', contrastText: '#ffffff' },
};

const DARK: Record<StatusKey, StatusColour> = {
  success: { main: '#5da900', light: '#1b241b', dark: '#6fbc2a', contrastText: '#121316' },
  info: { main: '#3a87fb', light: '#182032', dark: '#5d9cfe', contrastText: '#121316' },
  warning: { main: '#ff8f61', light: '#30211e', dark: '#ffae8e', contrastText: '#121316' },
  error: { main: '#ef5255', light: '#2e1b1e', dark: '#ff6d6c', contrastText: '#121316' },
};

export const statusColours = (mode: ThemeMode): Record<StatusKey, StatusColour> => (mode === 'dark' ? DARK : LIGHT);
